import React, { useMemo, useState } from 'react'
import Header from './components/Header'
import Footer from './components/Footer'
import PageHero from './components/PageHero'
import { getAwards, getFestivals } from '../data/loadData'
import { lower, upper } from '../data/dates'
import './Award.css'

const LOCALE = 'tr-TR'
const HONORIFIC = /^((?:Prof\.\s*Dr\.|Doç\.\s*Dr\.|Dr\.)\s*)/i

const titleCase = (value) => lower(value)
  .split(/\s+/)
  .map((word) => word.charAt(0).toLocaleUpperCase(LOCALE) + word.slice(1))
  .join(' ')

function parseAward(award, editionYears) {
  const label = String(award.topluluk || '').replace(/[\u2060\u00a0]/g, ' ').replace(/\s+/g, ' ').trim()
  const match = label.match(/^(\d+)\.\s*(.*)$/)
  const edition = match ? Number(match[1]) : null
  const kind = (match ? match[2] : label)
    .replace(/ethos/i, '')
    .replace(/ankara uluslararası tiyatro festivali/i, '')
    .replace(/odülü/i, 'ödülü')
    .trim()
  const name = String(award.oyun || '').trim()
  const honorific = name.match(HONORIFIC)
  return {
    key: award.id,
    edition,
    year: edition ? editionYears[edition] : null,
    kind: titleCase(kind),
    honorific: honorific ? honorific[1].trim() : '',
    name: honorific ? name.slice(honorific[1].length).trim() : name,
    photo: award.afis ? `images/${award.afis}` : '',
  }
}

export default function Award() {
  const [filter, setFilter] = useState('all')

  const awards = useMemo(() => {
    const editionYears = {}
    getFestivals().forEach((festival) => {
      const match = String(festival.name).match(/^(\d+)\./)
      if (match) editionYears[Number(match[1])] = festival.id
    })
    return getAwards()
      .map((award, order) => ({ ...parseAward(award, editionYears), order }))
      .sort((a, b) => (b.edition || 0) - (a.edition || 0) || a.order - b.order)
  }, [])

  const kinds = useMemo(() => {
    const counts = new Map()
    awards.forEach((award) => counts.set(award.kind, (counts.get(award.kind) || 0) + 1))
    return [...counts.entries()].sort((a, b) => b[1] - a[1])
  }, [awards])

  const editions = new Set(awards.map((award) => award.edition).filter(Boolean)).size
  const visible = filter === 'all' ? awards : awards.filter((award) => award.kind === filter)

  return (
    <React.Fragment>
      <Header />
      <PageHero
        title='Ethos ödülleri'
        lede={`${editions} festivalde ${awards.length} isim. Ethos Ankara Tiyatro Festivali'nin sahne sanatlarına katkı sunanlara verdiği onur, akademik hizmet, emek ve dostluk ödülleri.`}
      />

      <section className='aw' lang='tr' aria-label='Ödül alanlar'>
        <div className='container'>
          <div className='aw-filters' role='group' aria-label='Ödül türüne göre süz'>
            {[['all', awards.length], ...kinds].map(([kind, count]) => (
              <button
                key={kind}
                type='button'
                className='aw-filter'
                aria-pressed={filter === kind}
                onClick={() => setFilter(kind)}
              >
                {kind === 'all' ? 'Tümü' : kind}
                <span className='aw-filter-count'>{count}</span>
              </button>
            ))}
          </div>

          <ul className='aw-grid'>
            {visible.map((award) => (
              <li key={award.key} className='aw-card'>
                <div className='aw-photo'>
                  {award.photo ? <img src={award.photo} alt={`${award.honorific} ${award.name}`.trim()} loading='lazy' /> : null}
                  {award.edition ? (
                    <span className='aw-edition'>
                      <span className='aw-edition-num'>{award.edition}.</span>
                      {award.year ? <span className='aw-edition-year'>{award.year}</span> : null}
                      <span className='sr-only'> Ethos Ankara Tiyatro Festivali</span>
                    </span>
                  ) : null}
                </div>
                <div className='aw-caption'>
                  {award.honorific ? <p className='aw-honorific'>{award.honorific}</p> : null}
                  <h2 className='aw-name'>{upper(award.name)}</h2>
                  <p className='aw-kind'>{award.kind}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
      <Footer />
    </React.Fragment>
  )
}
